import { defineLocalStore } from '@/utils/localStore'
import { computed, reactive, shallowRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { confirm, handleAsync, setSnackbar } from 'best-modules/plugins'
import { useBreadcrumb } from '@/plugins'
import { required } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import { openDialog } from '@/dialog'
import { Audit, AuditDTO, AuditObjectDepartment } from '@/utils/types/audit'
import { notifyValidation } from '@/utils/helpers'
import { useCan } from '@/composables/useCan'
import { pick } from 'lodash'
import { cachedObject } from 'best-modules/utils'
import {
  auditDocumentUpdate,
  auditFlawDelete,
  auditObjectDepartmentCreate,
  auditObjectDepartmentDelete,
  auditObjectDepartmentList,
  auditRiskDelete,
  auditSingle,
  auditTakeToWork,
  auditUpdate,
} from '@/requests/audit'

export const { useLocalStore: useAudit, initLocalStore: initAudit } =
  defineLocalStore('audit', () => {
    const route = useRoute()
    const router = useRouter()
    const breadcrumb = useBreadcrumb()
    const { can } = useCan()
    const auditId = computed<number>(() => {
      return Number(route.params.id)
    })
    const audit = reactive<Audit>({} as Audit)
    const auditDTO = cachedObject<AuditDTO>({
      name: null,
      startAuditDate: null,
      endAuditDate: null,
      startScheduledCheckDate: null,
      endScheduledCheckDate: null,
      auditProgram: null,
      auditReport: null,
      auditAnnouncement: null,
      otherDocuments: null,
      description: null,
      objectTypeId: null,
      year: null,
    })
    auditDTO.$setObserve(
      'auditProgram',
      'auditReport',
      'auditAnnouncement',
      'otherDocuments'
    )
    const setAudit = (obj: Audit) => {
      Object.assign(audit, obj)
      auditDTO.$set(
        pick(obj, [
          'name',
          'startAuditDate',
          'endAuditDate',
          'startScheduledCheckDate',
          'endScheduledCheckDate',
          'auditProgram',
          'auditReport',
          'auditAnnouncement',
          'otherDocuments',
          'description',
          'objectTypeId',
          'year',
        ])
      )
    }
    const objectDepartments = shallowRef<AuditObjectDepartment[]>([])

    const rules = {
      name: { required },
      startAuditDate: { required },
      endAuditDate: { required },
      startScheduledCheckDate: { required },
      endScheduledCheckDate: { required },
    }

    const v$ = useVuelidate(rules, auditDTO)

    const getAudit = () => {
      return handleAsync('getAudit', () => {
        return auditSingle(auditId.value)
          .then(res => {
            setAudit(res)

            breadcrumb.set([
              {
                index: 1,
                title: audit.name,
                disabled: true,
              },
            ])
          })
          .catch(() => {
            router.push({ name: 'audit-list' })
          })
      })
    }
    const submit = () => {
      v$.value.$validate()
      if (!v$.value.$invalid) {
        return handleAsync('auditUpdate', () => {
          return auditUpdate(auditId.value, auditDTO.$object).then(res => {
            setAudit(res)
            setSnackbar({ text: 'Дані збережені' })
          })
        })
      } else {
        notifyValidation(v$.value)
      }
    }

    const updateAuditDocuments = (field: string, event: File) => {
      return auditDocumentUpdate(auditId.value, {
        [field]: [event],
      }).then(res => {
        setAudit(res)
        setSnackbar({ text: 'Документи збережено' })
      })
    }

    const getObjectDepartments = () => {
      return handleAsync('getObjectDepartments', () => {
        return auditObjectDepartmentList(auditId.value).then(res => {
          objectDepartments.value = res
        })
      })
    }

    const createObjectDepartment = (objectDepartmentId: number) => {
      return handleAsync(`createObjectDepartment_${objectDepartmentId}`, () => {
        return auditObjectDepartmentCreate({
          auditId: auditId.value,
          internalDepartmentId: objectDepartmentId,
        }).then(res => {
          objectDepartments.value.push(res)
        })
      })
    }
    const deleteObjectDepartment = (departmentId: number) => {
      return handleAsync(`deleteObjectDepartment_${departmentId}`, () => {
        return auditObjectDepartmentDelete(departmentId).then(() => {
          objectDepartments.value = objectDepartments.value.filter(
            o => o.id !== departmentId
          )
        })
      })
    }

    const takeToWork = () => {
      return handleAsync('auditTakeToWork', () => {
        return auditTakeToWork(auditId.value).then(res => {
          setSnackbar({ text: 'Аудит взято в роботу' })
          setAudit(res)
        })
      })
    }

    const completeAudit = () => {
      v$.value.$validate()
      if (!v$.value.$invalid) {
        openDialog({
          name: 'AuditComplete',
          dialogData: { auditId: auditId.value },
          params: {
            title: 'Завершення аудиту',
            subtitle: 'Вкажіть оцінку внутрішнього контролю',
            cb: res => {
              setAudit(res.data)
            },
          },
        })
      }
    }

    const createFlaw = () => {
      if (audit.statusId !== 2) {
        return setSnackbar({
          color: 'warning',
          text: 'Щоб додати факт недоліку потрібно взяти в роботу аудит',
        })
      }
      openDialog({
        name: 'AuditFlaw',
        action: 'create',
        dialogData: {
          auditId: auditId.value,
        },
        params: {
          cb: flaw => {
            audit.flaws.push({
              ...flaw,
              recommendations: [],
            })
          },
        },
      })
    }

    const updateFlaw = (flawId: number) => {
      can('audit-flaws.update', () => {
        const flaw = audit.flaws.find(f => f.id === flawId)

        openDialog({
          name: 'AuditFlaw',
          action: 'update',
          dialogData: {
            flaw,
          },
          params: {
            cb: updatedFlaw => {
              Object.assign(flaw, updatedFlaw)
            },
          },
        })
      })
    }

    const deleteFlaw = (flawId: number) => {
      can('audit-flaws.delete', () => {
        confirm({ text: 'Ви дійсно бажаєте видалити факт недоліку?' }).then(
          () => {
            return handleAsync(`deleteFlaw_${flawId}`, () => {
              return auditFlawDelete(flawId).then(() => {
                audit.flaws = audit.flaws.filter(itm => itm.id !== flawId)
                setSnackbar({ text: 'Факт недоліку видалено' })
              })
            })
          }
        )
      })
    }

    const createRisk = (flawId: number) => {
      can('audit-flaw-risks.create', () => {
        openDialog({
          name: 'AuditRisk',
          dialogData: {
            flawId: flawId,
          },
          params: {
            cb: risk => {
              const flaw = audit.flaws.find(f => f.id === flawId)

              flaw.risks.push(risk)
            },
          },
        })
      })
    }

    const deleteRisk = (flawId: number, riskId: number) => {
      can('audit-flaw-risks.delete', () => {
        return handleAsync(`removeRisk_${riskId}`, () => {
          return auditRiskDelete(riskId).then(() => {
            setSnackbar({ text: 'Ризк видалено' })
            const flaw = audit.flaws.find(f => f.id === flawId)
            flaw.risks = flaw.risks.filter(r => r.id !== riskId)
          })
        })
      })
    }

    const createRecommendation = (flawId: number) => {
      can('audit-recommendations.create', () => {
        openDialog({
          name: 'AuditRecommendation',
          dialogData: {
            auditFlawId: flawId,
          },
          params: {
            cb: recommendation => {
              const flaw = audit.flaws.find(f => f.id === flawId)
              if (!flaw.recommendations) {
                flaw.recommendations = []
              }
              flaw.recommendations.push(recommendation)
            },
          },
        })
      })
    }

    const setRecommendationStatus = (
      flawId: number,
      recommendationId: number
    ) => {
      openDialog({
        name: 'AuditRecommendationSetStatus',
        dialogData: { recommendationId },
        params: {
          cb: recommendation => {
            const flaw = audit.flaws.find(f => f.id === flawId)

            flaw.recommendations = flaw.recommendations.map(r => {
              if (r.id === recommendation.id) {
                return recommendation
              } else {
                return r
              }
            })
          },
        },
      })
    }

    getAudit()
    getObjectDepartments()

    return {
      auditId,
      audit,
      auditDTO,
      objectDepartments,
      v$,
      submit,
      updateAuditDocuments,
      takeToWork,
      completeAudit,
      createObjectDepartment,
      deleteObjectDepartment,
      createFlaw,
      updateFlaw,
      deleteFlaw,
      createRisk,
      deleteRisk,
      createRecommendation,
      setRecommendationStatus,
    }
  })
